import * as React from 'react';
import './featuredInsightField.scss';
import { FieldGroup } from '../FieldGroup/fieldGroup';
import { FeaturedInsightModal } from './featuredInsightModal';
import { formatDate, getSchema, parseDate } from '../../../utils';
import { useSelector } from '../../../utils/useSelector';
import { NodeType } from '../../../enums/NodeType';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { Metadata } from '../../../types';
import { ItemLink } from '../../ItemLink/itemLink';
import { FieldProps } from '../metadataFormField';
import TooltipPopover from '../../Popover/popover';
import { HistoryIcon } from '../../Icon/icon';

export const FeaturedInsightField = (props: FieldProps) => {
  const PriorityJourneyStepSchema = useSelector(s =>
    getSchema(
      s.planning.selectedWorkspace?.schemas,
      NodeType.PriorityJourneyStep
    )
  );
  const [showModal, setShowModal] = React.useState(false);
  const { data, updater } = props;
  const { updateField, update } = updater;
  const currentUser = useSelector(s => s.app?.user);
  const currentEmpCd = useSelector(s => s.app?.user?.employeeId);

  const featuredInsights = data?.props?.featuredInsights || [];

  const activeName =
    [currentUser?.firstName, currentUser?.lastName]
      .filter(x => !!x)
      .join(' ')
      .trim() || null;

  const updateFeaturedInsights = async (newInsights: Metadata[]) => {
    const field = findOne(PriorityJourneyStepSchema?.props?.fields, {
      field: 'featuredInsights'
    });
    await updateField(field, newInsights);
    await update();
  };

  const handleAddInsights = async (selectedItems: Metadata[]) => {
    const existingRefIds = new Set(
      featuredInsights.map(x => x?.props?.refId).filter(x => !!x)
    );
    const now = new Date();
    const selectedItemsWithRelationshipProps = selectedItems.map(insight => {
      const refId = insight?.props?.refId;
      const existingInsight = featuredInsights.find(
        x => x?.props?.refId === refId
      );
      const existingRelationshipProps = (existingInsight?.relationship?.props ||
        {}) as any;
      const incomingRelationshipProps = (insight?.relationship?.props ||
        {}) as any;
      const isNewlyAdded = !!refId && !existingRefIds.has(refId);

      const relationshipProps: any = {
        field: 'primaryPriorityJourneyStep',
        type: 'P',
        ...existingRelationshipProps,
        ...incomingRelationshipProps
      };

      if (isNewlyAdded) {
        if (!relationshipProps?.addedByEmpCd && currentEmpCd) {
          relationshipProps.addedByEmpCd = currentEmpCd;
        }
        if (!relationshipProps?.addedDateTime) {
          relationshipProps.addedDateTime = now;
        }
      }

      return {
        ...insight,
        relationship: {
          ...(insight?.relationship || {}),
          props: relationshipProps
        }
      } as Metadata;
    });

    await updateFeaturedInsights(selectedItemsWithRelationshipProps);
  };

  const getInsightAttribution = (insight: Metadata): string => {
    const relationshipProps = (insight?.relationship?.props || {}) as any;
    const addedByEmpCd = relationshipProps?.addedByEmpCd as number | null;
    const addedDateTime = relationshipProps?.addedDateTime;
    const addedByEmpName =
      (relationshipProps?.addedByEmpName as string | null) || null;

    if (!addedByEmpCd && !addedDateTime) {
      return null;
    }

    const displayName =
      addedByEmpName ||
      (addedByEmpCd && addedByEmpCd === currentEmpCd ? activeName : null);
    const userDisplay = displayName
      ? addedByEmpCd
        ? `(${addedByEmpCd}) ${displayName}`
        : displayName
      : addedByEmpCd
        ? `(${addedByEmpCd})`
        : null;

    const parsedDate = (() => {
      if (!addedDateTime) return null;
      if (
        typeof addedDateTime === 'string' ||
        typeof addedDateTime === 'number'
      ) {
        const d = new Date(addedDateTime);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      return parseDate(addedDateTime as any);
    })();
    const dateDisplay = parsedDate
      ? `${formatDate(parsedDate)} at ${parsedDate.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit'
        })}`
      : null;

    if (userDisplay && dateDisplay) {
      return `${userDisplay} on ${dateDisplay}`;
    }
    return userDisplay || dateDisplay;
  };

  return (
    <FieldGroup
      {...props}
      value={null}
      disabled={false}
      componentRight={
        !props.disabled ? (
          <div className="fgl-link" onClick={() => setShowModal(true)}>
            {featuredInsights.length > 0 ? 'Edit' : 'Add'} Featured Insights
          </div>
        ) : null
      }
    >
      <div className="relationship-container"></div>

      {showModal && (
        <FeaturedInsightModal
          data={data}
          onClose={() => setShowModal(false)}
          onSave={handleAddInsights}
        />
      )}

      <div className="selected-featured-insights">
        {featuredInsights.length > 0 ? (
          <div className="featured-insight-display">
            <ul className="featured-insight-group">
              {featuredInsights.map((insight: Metadata) => (
                <li key={insight.props?.refId}>
                  {(() => {
                    const attribution = getInsightAttribution(insight);
                    return (
                      <div className="featured-insight-row">
                        <span className="featured-insight-bullet">-</span>
                        <div className="featured-insight-link">
                          <ItemLink
                            refId={insight.props?.refId}
                            title={insight.props?.title}
                          />
                        </div>
                        {!!attribution && (
                          <div className="featured-insight-audit-icon">
                            <TooltipPopover
                              type="info"
                              packed={true}
                              pointerPosition="top-end"
                              content={attribution}
                            >
                              <HistoryIcon />
                            </TooltipPopover>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div>--</div>
        )}
      </div>
    </FieldGroup>
  );
};
